import 'bootstrap-fileinput/css/fileinput.min.css';
import 'bootstrap-fileinput/js/plugins/buffer.min';
import 'bootstrap-fileinput/js/plugins/filetype.min';
import 'bootstrap-fileinput/js/plugins/piexif.min';
import 'bootstrap-fileinput';
import 'bootstrap-fileinput/js/locales/cs.js';
import sortable from 'bootstrap-fileinput/js/plugins/sortable.min';
import { initFileInputCropper } from './file-input-cropper';
import { initLucideIcons } from './lucide-icons';
import {
    FILE_INPUT_BROWSE_BUTTON_CLASS,
    FILE_INPUT_DANGER_BUTTON_CLASS,
    FILE_INPUT_HIDDEN_ACTION_CLASS,
    FILE_INPUT_PREVIEW_ACTION_BUTTON_CLASS,
    FILE_INPUT_PREVIEW_ERROR_ACTION_BUTTON_CLASS,
    FILE_INPUT_TOOLBAR_BUTTON_CLASS,
    getFileInputIconOptions,
    getFileInputLayoutTemplates,
} from './file-input-layout';

type JsonValue = unknown[] | Record<string, unknown>;
type FileSortParams = {
    newIndex?: number;
    previewId?: string;
};
type SortableConstructor = {
    create: (element: HTMLElement, options: Record<string, unknown>) => {
        destroy: () => void;
    };
};
type SortableInstance = ReturnType<SortableConstructor['create']>;
type FileInputInstance = {
    _initSortable?: () => void;
    sortable?: SortableInstance;
};

const METADATA_NAMESPACE = '.naisFileInputMetadata';
const REMOVE_INTENT_NAMESPACE = '.naisFileInputRemoveIntent';
const REMOVE_INTENT_DATA = 'nais-file-remove-intent';
const PREVIEW_OBSERVER_DATA = 'nais-file-preview-observer';
const SORTABLE_INSTANCE_DATA = 'nais-file-sortable-instance';
const SORTABLE_DRAGGING_DATA = 'nais-file-sortable-dragging';
const SORTABLE_PENDING_REFRESH_DATA = 'nais-file-sortable-pending-refresh';
const SORTABLE_REFRESH_TIMER_DATA = 'nais-file-sortable-refresh-timer';
const ZOOM_CACHE_PREPARED_DATA = 'nais-file-zoom-cache-prepared';
const PENDING_UPLOAD_DATA = 'nais-file-pending-upload';
const ZOOM_CACHE_TEMPLATE_MARKER = 'kvTemp__2873389129__';
const SUPPORTED_CLIENT_FILE_TYPES = [
    'image',
    'html',
    'office',
    'gdocs',
    'text',
    'video',
    'audio',
    'flash',
    'pdf',
    'object',
    'other',
];

function isTruthyData(value: unknown): boolean {
    return value === true || value === 1 || value === '1' || value === 'true';
}

function parseJsonData(value: unknown, fallback: JsonValue): JsonValue {
    if (Array.isArray(value) || (value && typeof value === 'object')) {
        return value as JsonValue;
    }

    if (typeof value !== 'string' || value.trim() === '') {
        return fallback;
    }

    try {
        return JSON.parse(value) as JsonValue;
    } catch {
        return fallback;
    }
}

function parseUploadResponse(response: unknown): Record<string, unknown> {
    if (response && typeof response === 'object') {
        return response as Record<string, unknown>;
    }

    if (typeof response !== 'string' || response.trim() === '') {
        return {};
    }

    try {
        const parsed = JSON.parse(response);

        return parsed && typeof parsed === 'object' ? parsed as Record<string, unknown> : {};
    } catch {
        return {};
    }
}

function getScopedInputs(root: ParentNode | Document): JQuery {
    const selector = '[data-nais-file-input-upload]';
    const $root = $(root);
    const $inputs = $root.find(selector);

    return ($root.is(selector) ? $inputs.add($root) : $inputs).filter(function() {
        return $(this).closest('.newItem, .newTableRow').length === 0;
    });
}

function isSortableConstructor(value: unknown): value is SortableConstructor {
    const candidate = value as Partial<SortableConstructor> | null;

    return !!candidate
        && typeof candidate.create === 'function';
}

function resolveSortableConstructor(): SortableConstructor | null {
    if (isSortableConstructor(sortable)) {
        return sortable;
    }

    const candidate = sortable as { default?: unknown };
    if (isSortableConstructor(candidate.default)) {
        return candidate.default;
    }

    return null;
}

function ensureSortableBridge(): boolean {
    if (isSortableConstructor(window.Sortable)) {
        return true;
    }

    const sortableConstructor = resolveSortableConstructor();
    if (!sortableConstructor) {
        return false;
    }

    window.Sortable = sortableConstructor;

    return true;
}

function normalizePreviewConfig(config: JsonValue): unknown[] {
    if (!Array.isArray(config)) {
        return [];
    }

    return config.map((item: Record<string, unknown>) => {
        const mimeType = String(item.type || item.filetype || item.mimetype || '');
        const caption = String(item.caption || item.filename || '').toLowerCase();

        if (mimeType === 'image' || /\.(jpe?g|png|webp|gif|bmp|tiff?|svg)$/i.test(caption)) {
            return { ...item, type: 'image' };
        }

        return { ...item, type: 'other' };
    });
}

function getSupportedClientFileTypes(types: unknown[]): string[] {
    return types
        .map(type => String(type) === 'archive' ? 'object' : String(type))
        .filter(type => SUPPORTED_CLIENT_FILE_TYPES.includes(type));
}

function getDescriptionLanguages(value: unknown): string[] {
    const languages = parseJsonData(value, ['cs', 'en']);

    if (!Array.isArray(languages)) {
        return ['cs', 'en'];
    }

    const normalized = languages
        .map(lang => String(lang).trim())
        .filter(lang => lang !== '');

    return normalized.length ? normalized : ['cs', 'en'];
}

function escapeHtml(value: string): string {
    return value
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

function getPreviewData($input: JQuery<HTMLInputElement>): Record<string, unknown> {
    try {
        return ($input.fileinput('getPreview') || {}) as Record<string, unknown>;
    } catch {
        return {};
    }
}

function getPreviewConfig(previewData: Record<string, unknown>, index: number): Record<string, unknown> {
    const config = Array.isArray(previewData.config) ? previewData.config[index] : null;

    return config && typeof config === 'object' ? config as Record<string, unknown> : {};
}

function getFrameIndex($frame: JQuery): number {
    const scopedIndex = $frame
        .closest('.file-preview-thumbnails')
        .children('.file-preview-frame')
        .not('.kv-zoom-thumb')
        .index($frame);

    if (scopedIndex >= 0) {
        return scopedIndex;
    }

    return $frame.closest('.file-preview').find('.file-preview-frame').index($frame);
}

function normalizeFileId(value: unknown): string {
    const fileId = String(value ?? '').trim();

    return /^[1-9]\d*$/.test(fileId) ? fileId : '';
}

function getConfigFileId(config: Record<string, unknown>): string {
    return normalizeFileId(config.fileId || config.entityId || config.key);
}

function getSortablePreviewFrames($input: JQuery<HTMLInputElement>): JQuery {
    return $input.closest('.file-input')
        .find('.file-preview-thumbnails')
        .first()
        .children('.file-preview-frame')
        .not('.kv-zoom-thumb');
}

function getFrameFileId($input: JQuery<HTMLInputElement>, $frame: JQuery): string {
    const fileId = normalizeFileId($frame.attr('data-fileid') || $frame.data('fileid'));
    if (fileId) {
        return fileId;
    }

    const removeKey = normalizeFileId($frame.find('.kv-file-remove').attr('data-key'));
    if (removeKey) {
        return removeKey;
    }

    const frameIndex = getFrameIndex($frame);
    if (frameIndex < 0) {
        return '';
    }

    return getConfigFileId(getPreviewConfig(getPreviewData($input), frameIndex));
}

function getSortedFrameData($input: JQuery<HTMLInputElement>): Array<{ $frame: JQuery; fileId: string }> {
    return getSortablePreviewFrames($input).map((_index, frame) => {
        const $frame = $(frame);

        return {
            $frame,
            fileId: getFrameFileId($input, $frame),
        };
    }).get().filter(item => item.fileId !== '');
}

function getMovedFrameFromSortParams($input: JQuery<HTMLInputElement>, params: FileSortParams): JQuery {
    if (params.previewId) {
        const $frame = getPreviewFrameById($input, params.previewId);

        if ($frame.length) {
            return $frame;
        }
    }

    if (typeof params.newIndex === 'number') {
        return getSortablePreviewFrames($input).eq(params.newIndex);
    }

    return $();
}

function getPreviewFrameById($input: JQuery<HTMLInputElement>, previewId: string): JQuery {
    return $input.closest('.file-input')
        .find('.file-preview-frame')
        .filter(function() {
            return this.id === previewId;
        }).first();
}

function deleteUploadedSuccessThumb(
    $input: JQuery<HTMLInputElement>,
    $hidden: JQuery,
    deleteUrl: string,
    token: string,
    isGallery: boolean,
    previewId: string
): void {
    const $frame = getPreviewFrameById($input, previewId);
    const fileId = getFrameFileId($input, $frame);
    const hiddenValue = normalizeFileId($hidden.val());

    if (!deleteUrl || !fileId) {
        return;
    }

    $.nette.ajax({
        type: 'POST',
        url: deleteUrl,
        data: {
            token,
            key: fileId,
            fileId,
            value: hiddenValue,
            galleryId: isGallery ? hiddenValue : '',
        },
        success(response: { error?: boolean; errorMessage?: string }) {
            if (response?.error) {
                window.throwPNotify?.(String(response.errorMessage || 'Delete failed.'), 'error');
                return;
            }

            if (!isGallery && hiddenValue === fileId) {
                $hidden.val('').trigger('change');
            }
        },
        error() {
            window.throwPNotify?.('Nepodařilo se odstranit soubor.', 'error');
        },
    });
}

function bindUploadedRemoveIntent($input: JQuery<HTMLInputElement>): void {
    const $fileInput = $input.closest('.file-input');

    if (!$fileInput.length) {
        return;
    }

    $fileInput.off(`click${REMOVE_INTENT_NAMESPACE}`, '.kv-file-remove');
    $fileInput.on(`click${REMOVE_INTENT_NAMESPACE}`, '.kv-file-remove', function() {
        $input.data(REMOVE_INTENT_DATA, $(this).closest('.file-preview-frame').attr('id') || '');
    });
}

function submitGallerySort(
    $input: JQuery<HTMLInputElement>,
    token: string,
    sortUrl: string,
    galleryId: string,
    itemId: string,
    prevId: string,
    nextId: string
): void {
    if (!sortUrl || !galleryId || !itemId) {
        return;
    }

    const signature = [galleryId, itemId, prevId || '0', nextId || '0'].join(':');
    if ($input.data('nais-last-sort-signature') === signature) {
        return;
    }

    $input.data('nais-last-sort-signature', signature);

    $.nette.ajax({
        type: 'POST',
        url: sortUrl,
        data: {
            token,
            galleryId,
            itemId,
            prevId: prevId || 0,
            nextId: nextId || 0,
        },
        success(response: { error?: boolean; errorMessage?: string }) {
            if (response?.error) {
                window.throwPNotify?.(String(response.errorMessage || 'Sort failed.'), 'error');
                $input.removeData('nais-last-sort-signature');
            }
        },
        error() {
            window.throwPNotify?.('Nepodařilo se uložit pořadí souborů.', 'error');
            $input.removeData('nais-last-sort-signature');
        },
    });
}

function persistGallerySort(
    $input: JQuery<HTMLInputElement>,
    $hidden: JQuery,
    token: string,
    sortUrl: string,
    $movedFrame: JQuery = $()
): void {
    const galleryId = normalizeFileId($hidden.val());
    const items = getSortedFrameData($input);
    let itemId = $movedFrame.length ? getFrameFileId($input, $movedFrame) : '';

    if (!itemId) {
        return;
    }

    let index = items.findIndex(item => item.fileId === itemId);
    if (index < 0 && $movedFrame.length) {
        index = items.findIndex(item => item.$frame.get(0) === $movedFrame.get(0));
    }

    if (index < 0) {
        return;
    }

    submitGallerySort(
        $input,
        token,
        sortUrl,
        galleryId,
        itemId,
        items[index - 1]?.fileId || '',
        items[index + 1]?.fileId || ''
    );
}

function initOptionalCropper($input: JQuery<HTMLInputElement>): void {
    if (isTruthyData($input.data('crop-enabled'))) {
        initFileInputCropper($input);
    }
}

function clearUploadProgress($input: JQuery<HTMLInputElement>): void {
    const $fileInput = $input.closest('.file-input');
    const $progress = $fileInput.find('.kv-upload-progress, .file-thumb-progress');

    $progress.addClass('kv-hidden').hide();
    $progress.find('.progress-bar').text('');
    $fileInput.find('.file-preview-status').text('').hide();
}

function updateMetadataButtons($input: JQuery<HTMLInputElement>): void {
    const $fileInput = $input.closest('.file-input');

    if (!isTruthyData($input.data('metadata-enabled'))) {
        $fileInput.find('.kv-file-metadata').hide();
        return;
    }

    const previewData = getPreviewData($input);

    $fileInput.find('.file-preview-frame').each(function() {
        const $frame = $(this);
        if ($frame.hasClass('kv-zoom-thumb')) {
            return;
        }

        const index = getFrameIndex($frame);
        const config = getPreviewConfig(previewData, index);
        const fileId = getConfigFileId(config) || getFrameFileId($input, $frame);

        $frame.find('.kv-file-metadata').toggle(!!fileId);
    });
}

function refreshPreviewActions($input: JQuery<HTMLInputElement>, metadataLanguages: string[]): void {
    initOptionalMetadataModal($input, metadataLanguages);
    initOptionalCropper($input);
    updateMetadataButtons($input);
    stripZoomCachePlaceholders($input);
}

function sanitizeZoomCacheHtml(html: string): string {
    const safeHtml = html.replace(
        /\s(src|data)=(["'][^"']*kvTemp__2873389129__[^"']*["'])/gi,
        ' data-nais-zoom-$1-template=$2'
    );
    const $buffer = $('<div>').html(safeHtml);

    $buffer.find('input, textarea, select, datalist, form, .file-thumbnail-footer').remove();

    return $buffer.html() || '';
}

function stripZoomCachePlaceholders($input: JQuery<HTMLInputElement>): void {
    $input.closest('.file-input')
        .find('.kv-zoom-cache [src*="' + ZOOM_CACHE_TEMPLATE_MARKER + '"], '
            + '.kv-zoom-cache [data*="' + ZOOM_CACHE_TEMPLATE_MARKER + '"]')
        .each(function() {
            const $element = $(this);
            const src = $element.attr('src');
            const data = $element.attr('data');

            if (src && src.includes(ZOOM_CACHE_TEMPLATE_MARKER)) {
                $element.attr('data-nais-zoom-src-template', src).removeAttr('src');
            }

            if (data && data.includes(ZOOM_CACHE_TEMPLATE_MARKER)) {
                $element.attr('data-nais-zoom-data-template', data).removeAttr('data');
            }
        });
}

function prepareZoomCache($frame: JQuery): void {
    const previewId = String($frame.attr('id') || '');
    const encodedZoomData = String($frame.attr('data-zoom') || $frame.data('zoom') || '');

    if (!previewId || !encodedZoomData) {
        return;
    }

    let zoomUrl = encodedZoomData;
    try {
        zoomUrl = decodeURIComponent(encodedZoomData);
    } catch {
        // Keep original value when browser cannot decode a legacy preview URL.
    }

    const $zoomFrame = $frame.closest('.file-input').find('.kv-zoom-cache .file-preview-frame').filter(function() {
        return this.id === 'zoom-' + previewId;
    }).first();

    if (!$zoomFrame.length) {
        return;
    }

    $zoomFrame.find('[data-nais-zoom-src-template]').attr('src', zoomUrl);
    $zoomFrame.find('[data-nais-zoom-data-template]').attr('data', zoomUrl);
}

function bindZoomCachePreparation($input: JQuery<HTMLInputElement>): void {
    const container = $input.closest('.file-input').get(0);

    if (!container || $(container).data(ZOOM_CACHE_PREPARED_DATA)) {
        return;
    }

    container.addEventListener('click', event => {
        const target = event.target instanceof Element ? event.target : null;
        const button = target?.closest('.kv-file-zoom');

        if (!button || !container.contains(button)) {
            return;
        }

        prepareZoomCache($(button).closest('.file-preview-frame'));
    }, true);
    $(container).data(ZOOM_CACHE_PREPARED_DATA, true);
}

function refreshSortable(
    $input: JQuery<HTMLInputElement>,
    $hidden: JQuery,
    token: string,
    sortUrl: string,
    sortingEnabled: boolean
): void {
    if (!sortingEnabled) {
        return;
    }

    if ($input.data(SORTABLE_DRAGGING_DATA)) {
        $input.data(SORTABLE_PENDING_REFRESH_DATA, true);
        return;
    }

    const previousTimer = Number($input.data(SORTABLE_REFRESH_TIMER_DATA) || 0);
    if (previousTimer) {
        window.clearTimeout(previousTimer);
    }

    const timer = window.setTimeout(() => {
        $input.removeData(SORTABLE_REFRESH_TIMER_DATA);
        const previewElement = $input
            .closest('.file-input')
            .find('.file-preview-thumbnails')
            .first()
            .get(0);

        if (!previewElement || !getSortablePreviewFrames($input).length) {
            return;
        }

        if ($input.data(SORTABLE_DRAGGING_DATA)) {
            $input.data(SORTABLE_PENDING_REFRESH_DATA, true);
            return;
        }

        const fileInputInstance = $input.data('fileinput') as FileInputInstance | undefined;
        const previousInstance = $input.data(SORTABLE_INSTANCE_DATA) as SortableInstance | undefined;
        previousInstance?.destroy();
        $input.removeData(SORTABLE_INSTANCE_DATA);

        if (typeof fileInputInstance?._initSortable === 'function') {
            fileInputInstance._initSortable();
            previewElement.setAttribute('data-nais-sortable-active', 'fileinput');
            return;
        }

        const sortableConstructor = resolveSortableConstructor()
            || (isSortableConstructor(window.Sortable) ? window.Sortable : null);

        if (!sortableConstructor) {
            return;
        }

        const instance = sortableConstructor.create(previewElement, {
            handle: '.drag-handle-init',
            dataIdAttr: 'data-fileid',
            animation: 150,
            draggable: '.file-sortable',
            forceFallback: true,
            scroll: true,
            onChoose() {
                beginSortableDrag($input);
            },
            onStart() {
                beginSortableDrag($input);
            },
            onUnchoose() {
                endSortableDrag($input, $hidden, token, sortUrl, [], sortingEnabled);
            },
            onEnd(event: { item?: HTMLElement; newIndex?: number; oldIndex?: number }) {
                endSortableDrag($input, $hidden, token, sortUrl, [], sortingEnabled);

                if (event.item && event.oldIndex !== event.newIndex) {
                    persistGallerySort($input, $hidden, token, sortUrl, $(event.item));
                }
            },
        });

        previewElement.setAttribute('data-nais-sortable-active', 'sortablejs');
        $input.data(SORTABLE_INSTANCE_DATA, instance);
    }, 50);

    $input.data(SORTABLE_REFRESH_TIMER_DATA, timer);
}

function beginSortableDrag($input: JQuery<HTMLInputElement>): void {
    $input.data(SORTABLE_DRAGGING_DATA, true);
    $('body, html').addClass('file-grabbing');
}

function endSortableDrag(
    $input: JQuery<HTMLInputElement>,
    $hidden: JQuery,
    token: string,
    sortUrl: string,
    metadataLanguages: string[],
    sortingEnabled: boolean
): void {
    $('body, html').removeClass('file-grabbing');
    $input.removeData(SORTABLE_DRAGGING_DATA);

    if (!$input.data(SORTABLE_PENDING_REFRESH_DATA)) {
        return;
    }

    $input.removeData(SORTABLE_PENDING_REFRESH_DATA);
    window.setTimeout(() => {
        refreshPreviewBehavior($input, $hidden, token, sortUrl, metadataLanguages, sortingEnabled);
    }, 0);
}

function refreshPreviewBehavior(
    $input: JQuery<HTMLInputElement>,
    $hidden: JQuery,
    token: string,
    sortUrl: string,
    metadataLanguages: string[],
    sortingEnabled: boolean
): void {
    refreshPreviewActions($input, metadataLanguages);
    refreshSortable($input, $hidden, token, sortUrl, sortingEnabled);

    const fileInputRoot = $input.closest('.file-input').get(0);
    if (fileInputRoot?.querySelector('i[data-lucide]')) {
        initLucideIcons(fileInputRoot);
    }
}

function observePreviewMutations(
    $input: JQuery<HTMLInputElement>,
    $hidden: JQuery,
    token: string,
    sortUrl: string,
    metadataLanguages: string[],
    sortingEnabled: boolean
): void {
    if ($input.data(PREVIEW_OBSERVER_DATA)) {
        return;
    }

    const previewElement = $input.closest('.file-input').find('.file-preview-thumbnails').get(0);
    if (!previewElement) {
        return;
    }

    const observer = new MutationObserver(() => {
        if ($input.data(SORTABLE_DRAGGING_DATA)) {
            $input.data(SORTABLE_PENDING_REFRESH_DATA, true);
            return;
        }

        refreshPreviewBehavior($input, $hidden, token, sortUrl, metadataLanguages, sortingEnabled);
    });

    observer.observe(previewElement, {
        childList: true,
        subtree: true,
    });
    $input.data(PREVIEW_OBSERVER_DATA, observer);
}

function initOptionalMetadataModal($input: JQuery<HTMLInputElement>, languages: string[]): void {
    if (!isTruthyData($input.data('metadata-enabled'))) {
        return;
    }

    const $fileInput = $input.closest('.file-input');

    $fileInput.off('click' + METADATA_NAMESPACE, '.kv-file-metadata');
    $fileInput.on('click' + METADATA_NAMESPACE, '.kv-file-metadata', function(e) {
        e.preventDefault();

        const $frame = $(this).closest('.file-preview-frame');
        const index = getFrameIndex($frame);
        const previewData = getPreviewData($input);
        const config = getPreviewConfig(previewData, index);
        const fileId = getConfigFileId(config) || getFrameFileId($input, $frame);

        if (!fileId) {
            return;
        }

        openMetadataModal($input, String(fileId), config, languages, metadata => {
            const livePreviewData = getPreviewData($input);
            if (Array.isArray(livePreviewData.config) && livePreviewData.config[index]) {
                // metadata.description/alt are the per-language maps the server persists under
                // those same keys - they must land on "descriptionLangs"/"altLangs" here too, or
                // the next modal open reads the raw map back off "description"/"alt" and
                // bootstrap-fileinput's own zoom caption renders "[object Object]" (see
                // buildPreviewConfig). "alt" is deliberately left unset - the plugin reads that
                // key itself and, when present, uses it to overwrite the title it shows on
                // hover/zoom, silently replacing our description caption with the alt text.
                livePreviewData.config[index] = {
                    ...livePreviewData.config[index],
                    ...metadata,
                    description: pickNonEmptyMetadataValue(metadata.description),
                    descriptionLangs: metadata.description,
                    alt: undefined,
                    altLangs: metadata.alt,
                };
            }

            // bootstrap-fileinput's own zoom view doesn't read previewData.config again after
            // the initial render - it reads a copy the plugin cached on the frame itself the
            // first time round. Patch that copy too, or the new caption only shows up after a
            // full page reload.
            $frame.data('description', pickNonEmptyMetadataValue(metadata.description));
        });
    });
}

function openMetadataModal(
    $input: JQuery<HTMLInputElement>,
    fileId: string,
    config: Record<string, unknown>,
    languages: string[],
    onSaveCallback: (metadata: Record<string, Record<string, string>>) => void
): void {
    const modalId = `metadataModal-fileinput-${fileId}`;
    const existingModal = document.getElementById(modalId);

    if (existingModal) {
        hideModal(existingModal);
        existingModal.remove();
    }

    // "description"/"alt" on the preview config are the flat strings bootstrap-fileinput's
    // own zoom caption and title/alt attributes read - our per-language maps live under
    // "descriptionLangs"/"altLangs" instead.
    const description = normalizeMetadataLangMap(config.descriptionLangs);
    const alt = normalizeMetadataLangMap(config.altLangs);
    const tabsHtml = languages.map((lang, index) => (
        '<li class="nais-settings-system-page__tab-item nav-item" role="presentation">' +
            '<a class="nais-settings-system-page__tab nav-link' + (index === 0 ? ' active' : '') + '"' +
            ' data-bs-toggle="tab" href="#metadata-pane-' + escapeHtml(lang) + '-' + escapeHtml(fileId) + '"' +
            ' role="tab" aria-selected="' + (index === 0 ? 'true' : 'false') + '">' +
                '<i data-lucide="languages" aria-hidden="true"></i>' +
                '<span>' + escapeHtml(lang.toUpperCase()) + '</span>' +
            '</a>' +
        '</li>'
    )).join('');
    const panesHtml = languages.map((lang, index) => (
        '<div class="tab-pane fade' + (index === 0 ? ' show active' : '') + '"' +
            ' id="metadata-pane-' + escapeHtml(lang) + '-' + escapeHtml(fileId) + '" role="tabpanel">' +
            '<div class="mb-3">' +
                '<label class="form-label" for="metadata-description-' + escapeHtml(lang) + '-' + escapeHtml(fileId) + '">Popisek</label>' +
                '<textarea class="form-control metadata-description-input" rows="4"' +
                    ' id="metadata-description-' + escapeHtml(lang) + '-' + escapeHtml(fileId) + '"' +
                    ' data-lang="' + escapeHtml(lang) + '">' + escapeHtml(description[lang] || '') + '</textarea>' +
            '</div>' +
            '<div class="mb-0">' +
                '<label class="form-label" for="metadata-alt-' + escapeHtml(lang) + '-' + escapeHtml(fileId) + '">Alt text</label>' +
                '<input type="text" class="form-control metadata-alt-input"' +
                    ' id="metadata-alt-' + escapeHtml(lang) + '-' + escapeHtml(fileId) + '"' +
                    ' data-lang="' + escapeHtml(lang) + '"' +
                    ' value="' + escapeHtml(alt[lang] || '') + '">' +
            '</div>' +
        '</div>'
    )).join('');

    document.body.insertAdjacentHTML('beforeend', `
        <div class="comp__modal cms-metadata-modal modal fade" id="${modalId}" tabindex="-1" role="dialog" aria-hidden="true">
            <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Popisek a alt text</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body cms-metadata-modal__body">
                        <ul class="nais-settings-system-page__tabs nav nav-tabs bar_tabs" role="tablist">
                            ${tabsHtml}
                        </ul>
                        <div class="tab-content">
                            ${panesHtml}
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Zrušit</button>
                        <button type="button" class="btn btn-primary" id="metadata-confirm-${fileId}">Uložit</button>
                    </div>
                </div>
            </div>
        </div>
    `);

    const modal = document.getElementById(modalId);
    const confirmButton = document.getElementById(`metadata-confirm-${fileId}`);

    if (!modal || !confirmButton) {
        modal?.remove();
        return;
    }

    initLucideIcons(modal);

    confirmButton.addEventListener('click', () => {
        const metadata = collectMetadata(modal);

        $.nette.ajax({
            type: 'POST',
            url: String($input.data('metadata-url') || '?do=updateFileMetadata'),
            data: {
                token: String($input.data('token') || ''),
                fileId,
                metadataJson: JSON.stringify(metadata),
                galleryName: String($input.data('control-name') || ''),
            },
            success() {
                onSaveCallback(metadata);
                hideModal(modal);
            },
            error() {
                window.throwPNotify?.('Nepodařilo se uložit metadata souboru.', 'error');
            },
        });
    });

    modal.addEventListener('hidden.bs.modal', () => {
        modal.remove();

        if ($('#modalEditBlock, #modalAddBlock').filter('.show').length) {
            $('body').addClass('modal-open');
        }
    }, { once: true });

    showModal(modal);
}

/**
 * First non-empty language variant - mirrors FileInputUploadService::pickPreviewCaption()
 * on the PHP side, for plugins (bootstrap-fileinput's own zoom caption) that only
 * understand a single plain string, not our per-language map.
 */
function pickNonEmptyMetadataValue(value: unknown): string {
    const map = normalizeMetadataLangMap(value);
    const nonEmpty = Object.values(map).find((item) => item.trim() !== '');

    return nonEmpty || '';
}

function normalizeMetadataLangMap(value: unknown): Record<string, string> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return {};
    }

    const result: Record<string, string> = {};
    Object.entries(value as Record<string, unknown>).forEach(([key, item]) => {
        result[key] = String(item || '');
    });

    return result;
}

function collectMetadata(modal: HTMLElement): Record<string, Record<string, string>> {
    const description: Record<string, string> = {};
    const alt: Record<string, string> = {};

    $(modal).find('.metadata-description-input').each(function() {
        const lang = String($(this).data('lang') || '');
        if (lang) {
            description[lang] = String($(this).val() || '');
        }
    });

    $(modal).find('.metadata-alt-input').each(function() {
        const lang = String($(this).data('lang') || '');
        if (lang) {
            alt[lang] = String($(this).val() || '');
        }
    });

    return { description, alt };
}

function showModal(modal: HTMLElement): void {
    if (!window.bootstrap?.Modal) {
        window.throwPNotify?.('Bootstrap modal API není dostupné.', 'error');
        return;
    }

    window.bootstrap.Modal.getOrCreateInstance(modal).show();
}

function hideModal(modal: HTMLElement): void {
    if (!window.bootstrap?.Modal) {
        modal.remove();
        return;
    }

    window.bootstrap.Modal.getOrCreateInstance(modal).hide();
}

function initFileInputUpload(input: HTMLInputElement): void {
    const $input = $(input);

    if ($input.data('fileinput') || $input.closest('.file-input').length > 0) {
        return;
    }

    const mode = String($input.data('mode') || 'single');
    const $hidden = $($input.data('hidden-input'));
    const token = String($input.data('token') || '');
    const uploadUrl = String($input.data('upload-url') || '');
    const deleteUrl = String($input.data('delete-url') || '');
    const sortUrl = String($input.data('sort-url') || '');
    const locale = String($input.data('locale') || document.documentElement.lang || 'cs');
    const style = String($input.data('style') || 'compact');
    const initialPreview = parseJsonData($input.attr('data-initial-preview'), []) as unknown[];
    const initialPreviewConfig = normalizePreviewConfig(parseJsonData($input.attr('data-initial-preview-config'), []));
    const allowedExtensions = parseJsonData($input.attr('data-allowed-extensions'), []) as unknown[];
    const allowedFileTypes = parseJsonData($input.attr('data-allowed-file-types'), []) as unknown[];
    const clientAllowedFileTypes = getSupportedClientFileTypes(allowedFileTypes);
    const maxFileSize = Number($input.data('max-file-size') || 0);
    const isGallery = mode === 'gallery';
    const isCompact = style !== 'full';
    const sortingEnabled = isGallery && isTruthyData($input.data('nais-sortable')) && ensureSortableBridge();
    const cropEnabled = isTruthyData($input.data('crop-enabled'));
    const metadataEnabled = isTruthyData($input.data('metadata-enabled'));
    const metadataLanguages = getDescriptionLanguages($input.attr('data-description-langs'));

    $input.closest('.nais-fileinput-upload')
        .attr('data-nais-fileinput-style', style)
        .attr('data-nais-fileinput-sortable', sortingEnabled ? '1' : '0')
        .attr('data-nais-fileinput-crop', cropEnabled ? '1' : '0');

    const iconOptions = getFileInputIconOptions();

    $input.fileinput({
        ...iconOptions,
        language: locale,
        uploadAsync: true,
        maxAjaxThreads: 1,
        uploadUrl,
        deleteUrl,
        ajaxSettings: {
            dataType: 'json',
        },
        ajaxDeleteSettings: {
            dataType: 'json',
        },
        uploadExtraData: () => ({
            token,
            value: $hidden.val() || '',
            galleryId: $hidden.val() || '',
        }),
        deleteExtraData: () => ({
            token,
            value: $hidden.val() || '',
            galleryId: $hidden.val() || '',
        }),
        initialPreview,
        initialPreviewAsData: true,
        initialPreviewConfig,
        overwriteInitial: !isGallery,
        showClose: false,
        showCaption: false,
        showRemove: false,
        showCancel: null,
        showUpload: true,
        showUploadedThumbs: true,
        dropZoneEnabled: !isCompact,
        browseOnZoneClick: !isCompact,
        browseClass: FILE_INPUT_BROWSE_BUTTON_CLASS,
        uploadClass: FILE_INPUT_TOOLBAR_BUTTON_CLASS,
        cancelClass: FILE_INPUT_TOOLBAR_BUTTON_CLASS,
        removeClass: FILE_INPUT_DANGER_BUTTON_CLASS,
        allowedFileExtensions: allowedExtensions.length ? allowedExtensions : undefined,
        allowedFileTypes: clientAllowedFileTypes.length ? clientAllowedFileTypes : undefined,
        maxFileSize: maxFileSize > 0 ? maxFileSize : undefined,
        retryErrorUploads: false,
        sanitizeZoomCache: sanitizeZoomCacheHtml,
        previewFileIconSettings: {
            pdf: '<i data-lucide="file-text"></i>',
            doc: '<i data-lucide="file-text"></i>',
            docx: '<i data-lucide="file-text"></i>',
            xls: '<i data-lucide="file-spreadsheet"></i>',
            xlsx: '<i data-lucide="file-spreadsheet"></i>',
            csv: '<i data-lucide="file-spreadsheet"></i>',
            xml: '<i data-lucide="file-code"></i>',
        },
        fileActionSettings: {
            ...iconOptions.fileActionSettings,
            showDrag: sortingEnabled,
            showUpload: false,
            showRotate: false,
            showZoom: true,
            showDownload: false,
            rotateClass: FILE_INPUT_HIDDEN_ACTION_CLASS,
            uploadClass: FILE_INPUT_HIDDEN_ACTION_CLASS,
            downloadClass: FILE_INPUT_HIDDEN_ACTION_CLASS,
            dragClass: FILE_INPUT_PREVIEW_ACTION_BUTTON_CLASS,
            removeClass: FILE_INPUT_PREVIEW_ACTION_BUTTON_CLASS,
            zoomClass: FILE_INPUT_PREVIEW_ACTION_BUTTON_CLASS,
            removeErrorClass: FILE_INPUT_PREVIEW_ERROR_ACTION_BUTTON_CLASS,
            dragSettings: sortingEnabled ? {
                onChoose: () => beginSortableDrag($input),
                onStart: () => beginSortableDrag($input),
                onUnchoose: () => endSortableDrag(
                    $input,
                    $hidden,
                    token,
                    sortUrl,
                    metadataLanguages,
                    sortingEnabled
                ),
                onEnd: () => endSortableDrag(
                    $input,
                    $hidden,
                    token,
                    sortUrl,
                    metadataLanguages,
                    sortingEnabled
                ),
            } : {},
        },
        previewZoomButtonClasses: {
            toggleheader: FILE_INPUT_HIDDEN_ACTION_CLASS,
            fullscreen: FILE_INPUT_HIDDEN_ACTION_CLASS,
            borderless: FILE_INPUT_HIDDEN_ACTION_CLASS,
        },
        layoutTemplates: getFileInputLayoutTemplates({
            crop: cropEnabled,
            drag: sortingEnabled,
            metadata: metadataEnabled,
        }),
    });

    const zoomModal = document.getElementById('kvFileinputModal');
    if (zoomModal?.querySelector('i[data-lucide]')) {
        initLucideIcons(zoomModal);
    }

    $input.on('filezoomshown', (_event, params) => {
        const modal = params?.modal?.get?.(0) as HTMLElement | undefined;
        if (modal?.querySelector('i[data-lucide]')) {
            initLucideIcons(modal);
        }
    });

    bindUploadedRemoveIntent($input);
    bindZoomCachePreparation($input);
    observePreviewMutations($input, $hidden, token, sortUrl, metadataLanguages, sortingEnabled);
    refreshPreviewBehavior($input, $hidden, token, sortUrl, metadataLanguages, sortingEnabled);

    // A count of files picked but not yet resolved (uploaded or failed), not a plain flag -
    // a boolean here went to false the moment the FIRST file of a multi-file batch finished,
    // even with others still uploading, and also cleared on a failed upload (silently letting
    // the form save without ever warning that file was lost). Never mutate directly - always
    // through adjustPendingUploadCount() so it can't drift below zero.
    const adjustPendingUploadCount = (delta: number): void => {
        const next = Math.max(0, (Number($input.data(PENDING_UPLOAD_DATA)) || 0) + delta);
        $input.data(PENDING_UPLOAD_DATA, next);
    };

    // For AJAX upload (always true here) bootstrap-fileinput raises this with the file
    // manager's whole current stack, not just the newly-added files - already the
    // authoritative "how many are outstanding right now" count, so this sets rather than
    // adds, or picking a second batch on top of the first would double-count the first.
    // The stack is a plain object keyed by a generated file id (e.g. "73_test.png"), NOT
    // an array - Array.isArray() on it is always false, which silently kept this at 0
    // forever and made the whole guard a no-op regardless of form wiring.
    $input.on('filebatchselected', (_event, files: unknown) => {
        const count = files && typeof files === 'object' ? Object.keys(files).length : 0;
        $input.data(PENDING_UPLOAD_DATA, count);
    });

    // Removing one file only resolves that one file; clearing/cancelling the whole picker
    // (no per-file id involved) resolves everything still outstanding.
    $input.on('fileremoved', () => adjustPendingUploadCount(-1));
    $input.on('filecleared fileselectnone', () => $input.data(PENDING_UPLOAD_DATA, 0));

    $input.on('filepreupload', () => {
        $input.closest('.file-input').find('.kv-upload-progress').removeClass('kv-hidden').show();
    });

    $input.on('fileuploaded', (_event, data) => {
        adjustPendingUploadCount(-1);

        const response = parseUploadResponse(data.response);

        if (response.error) {
            window.throwPNotify?.(String(response.errorMessage || response.error || 'Upload failed.'), 'error');
            return;
        }

        if (isGallery && response.galleryId) {
            const galleryId = String(response.galleryId);
            $hidden.val(galleryId).trigger('change');
            $input.data('gallery-id', galleryId);
            clearUploadProgress($input);
            refreshPreviewBehavior($input, $hidden, token, sortUrl, metadataLanguages, sortingEnabled);
            return;
        }

        if (!isGallery && response.fileId) {
            $hidden.val(String(response.fileId)).trigger('change');
        }

        clearUploadProgress($input);
        refreshPreviewBehavior($input, $hidden, token, sortUrl, metadataLanguages, sortingEnabled);
    });

    $input.on('filedeleted', (_event, key) => {
        if (!isGallery) {
            $hidden.val('').trigger('change');
        }

        clearUploadProgress($input);
        refreshPreviewBehavior($input, $hidden, token, sortUrl, metadataLanguages, sortingEnabled);
    });

    $input.on('filesuccessremove', (_event, previewId) => {
        const intentPreviewId = String($input.data(REMOVE_INTENT_DATA) || '');
        const normalizedPreviewId = String(previewId || '');

        $input.removeData(REMOVE_INTENT_DATA);

        if (!intentPreviewId || intentPreviewId !== normalizedPreviewId) {
            return;
        }

        deleteUploadedSuccessThumb($input, $hidden, deleteUrl, token, isGallery, String(previewId || ''));
    });

    $input.on('fileloaded filebatchuploadcomplete', () => {
        clearUploadProgress($input);
        refreshPreviewBehavior($input, $hidden, token, sortUrl, metadataLanguages, sortingEnabled);
    });

    // Deliberately doesn't touch the pending count here: a failed file is still unsaved, so
    // it should keep blocking submit (see adjustPendingUploadCount) until the user removes it
    // or a retry succeeds - not silently wave the form through without it.
    $input.on('fileuploaderror', (_event, _data, message) => {
        window.throwPNotify?.(message || 'Upload failed.', 'error');
    });

    if (isGallery && sortingEnabled) {
        $input.on('filesorted', (_event, params: FileSortParams) => {
            persistGallerySort($input, $hidden, token, sortUrl, getMovedFrameFromSortParams($input, params || {}));
        });
    }
}

export function initFileInputUploads(root: ParentNode | Document = document): void {
    getScopedInputs(root).each((_index, input) => {
        initFileInputUpload(input as HTMLInputElement);
    });
}

/**
 * True if any file input inside root has a file picked but not yet sent via the
 * "Nahrát" button - saving now would silently discard it.
 */
export function hasPendingFileInputUpload(root: ParentNode | Document = document): boolean {
    return getScopedInputs(root).toArray().some((input) => (Number($(input).data(PENDING_UPLOAD_DATA)) || 0) > 0);
}

window.initFileInputUploads = initFileInputUploads;
window.hasPendingFileInputUpload = hasPendingFileInputUpload;

function blockSubmitWithPendingUpload(event: Event): void {
    event.preventDefault();
    event.stopImmediatePropagation();
    window.throwPNotify?.(
        'Vybraný soubor ještě není nahraný. Klikněte na "Nahrát", jinak se při uložení ztratí.',
        'error'
    );
}

// Guard EVERY plain form submit across the admin (module CRUD forms, gallery items, ...),
// not just the CMS block modals (those go through cmsBlockForms.js's own AJAX submit path
// and never fire a native "submit" event). Capturing phase so this runs before any other
// submit handler (Nette's live validation, naja/nette.ajax) gets to act on the event.
//
// This alone isn't enough for a plain "class=ajax" form though (StaticPages, and every
// other non-CMS-block admin form that submits via nette.ajax.js): clicking its submit
// button never dispatches a native "submit" event at all - nette.ajax.js intercepts the
// button's own click, preventDefault()s it, and calls Nette.validateForm() + its own AJAX
// request directly. So also intercept the click on any submit control, one capturing
// step earlier than nette.ajax's own (bubbling, bound directly on the button) handler.
document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !hasPendingFileInputUpload(form)) {
        return;
    }

    blockSubmitWithPendingUpload(event);
}, true);

document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) {
        return;
    }

    const submitControl = target.closest('button[type="submit"], input[type="submit"], input[type="image"]');
    // .form (not closest('form')) - several admin layouts place the submit button in a
    // sticky header OUTSIDE the <form> it saves, wired up only via its own form="id"
    // attribute. closest() only walks DOM ancestors and misses that association entirely;
    // the standard .form IDL property resolves both cases the same way the browser does
    // when actually submitting the button.
    const form = (submitControl as HTMLButtonElement | HTMLInputElement | null)?.form;
    if (!submitControl || !form || !hasPendingFileInputUpload(form)) {
        return;
    }

    blockSubmitWithPendingUpload(event);
}, true);
